import axios, { AxiosError } from 'axios';
import { IAdvertRepository, ListAdvertsParams, Paginated, CreateAdvertDTO, UpdateAdvertDTO } from '../../application/ports/IAdvertRepository';
import { AdvertEntity } from '../../domain/entities/Advert';
import { advertizerApiHost } from '../../constants/envs';
import { mapApiToEntity, AdvertAPIModel, ListAdvertsAPIResponse } from '../mappers/adverts.mapper';
import { TransportError, NotAuthenticatedError } from '../../shared/errors/BaseErrors';
import { AuthTokenProvider } from '../auth/AuthTokenProvider';

export class AxiosAdvertRepository implements IAdvertRepository {
  constructor(private auth: AuthTokenProvider) {}

  private async authHeaders() {
    const token = await this.auth.getToken();
    if (!token) throw new NotAuthenticatedError();
    return { Authorization: `Bearer ${token}` };
  }

  async get(uuid: string): Promise<AdvertEntity> {
    try {
      const res = await axios.get<AdvertAPIModel>(`${advertizerApiHost}/api/adverts/${uuid}`, { headers: await this.authHeaders() });
      return mapApiToEntity(res.data);
    } catch (e) { throw this.toTransportError(e); }
  }

  async list(params: ListAdvertsParams): Promise<Paginated<AdvertEntity>> {
    try {
      const res = await axios.get<ListAdvertsAPIResponse>(`${advertizerApiHost}/api/adverts`, { headers: await this.authHeaders(), params });
      return { items: res.data.adverts.map(mapApiToEntity), page: res.data.pagination.page, perPage: res.data.pagination.perPage, total: res.data.pagination.total };
    } catch (e) { throw this.toTransportError(e); }
  }

  async create(dto: CreateAdvertDTO): Promise<AdvertEntity> {
    try {
      const fd = new FormData();
      fd.append('title', dto.title);
      fd.append('description', dto.description);
      dto.attachments.forEach(a => {
        fd.append('attachments', a.data, a.fileName);
        fd.append(`attachments[${a.position}][position]`, a.position.toString());
      });
  const res = await axios.post<AdvertAPIModel>(`${advertizerApiHost}/api/adverts`, fd, { headers: { ...(await this.authHeaders()) } });
      return mapApiToEntity(res.data);
    } catch (e) { throw this.toTransportError(e); }
  }

  async update(dto: UpdateAdvertDTO): Promise<AdvertEntity> {
    try {
      const fd = new FormData();
      fd.append('title', dto.title);
      fd.append('description', dto.description);
      dto.attachments.forEach((a, i) => {
        fd.append('attachments', a.data, a.fileName);
        fd.append(`attachments[${i}][position]`, a.position.toString());
      });
      dto.keepAttachments.forEach(k => {
        fd.append('keepAttachments', k.uuid);
        fd.append(`keepAttachments[${k.uuid}][position]`, k.position.toString());
      });
  const res = await axios.put<AdvertAPIModel>(`${advertizerApiHost}/api/adverts/${dto.uuid}`, fd, { headers: { ...(await this.authHeaders()) } });
      return mapApiToEntity(res.data);
    } catch (e) { throw this.toTransportError(e); }
  }

  async delete(uuid: string): Promise<void> {
    try {
      await axios.delete(`${advertizerApiHost}/api/adverts/${uuid}`, { headers: await this.authHeaders() });
    } catch (e) { throw this.toTransportError(e); }
  }

  private toTransportError(error: unknown): TransportError {
    const axiosError = error as AxiosError<any>;
    const msg = axiosError.response?.data?.message || axiosError.message || 'Network error';
    return new TransportError(msg, axiosError.response?.status);
  }
}
